import React, { useState, useEffect } from 'react';
import { Formik, Form, yupToFormErrors } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';

import DocumentTemplateIcon from 'assets/document-template-icon.svg';
import { P, H2, ButtonPrimary } from '@yuhu-inc/yuhui';
import AdditionalFields from './form_fields/AdditionalFields';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

import { StyledFooterText } from './form_fields/shared_styles/footers';

const StyledForm = styled(Form)`
  width: ${(props) => (props.fieldForms ? '615px' : '330px')};
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 800px;
  ${media.mobile`
  width: 80%;
  `};
`;

const StyledBodyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledP = styled(P)`
  margin-top: 38px;
  font-size: 18px;
  color: ${colors.SLATE_GREY};
`;

const StyledButtonContainer = styled.div`
  width: 100%;
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(ButtonPrimary)`
  min-width: 140px;
  font-size: 18px !important;
`;

const StyledIcon = styled.img`
  width: 180px;
  height: 180px;
  margin-top: 15px;
  ${media.mobile`
    width: 130px;
    height: 130px;
  `};
`;

Yup.addMethod(Yup.array, 'unique', function (message) {
  return this.test('unique', message, function (list) {
    const mapper = (x) => x.label;
    const set = [...new Set(list.map(mapper))];
    const isUnique = list.length === set.length;

    const idx = list.findIndex((l, i) => mapper(l) !== set[i]);
    const focusIndex = this.parent.focusIndex;
    if (idx !== -1) {
      return this.createError({
        path: `${this.path}[${focusIndex}].label`,
        message: message,
      });
    }
    return isUnique;
  });
});

const AdditionalFieldsValidationSchema = {
  additional_fields: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required('Required'),
        field_type: Yup.string().required('Required'),
        default_value: Yup.string().required('Required'),
      })
    )
    .unique('Field name must be unique'),
};

const validateForm = async (values) => {
  const schema = Yup.object().shape(AdditionalFieldsValidationSchema);

  return schema.validate(values, { abortEarly: false }).catch((error) => {
    throw yupToFormErrors(error);
  });
};

export const DocumentTemplateAdditionalFieldsPage = ({
  forward,
  setTemplate,
  documentTemplate = {},
  ...rest
}) => {
  const [fieldForms, setFieldForms] = useState(false);

  useEffect(() => {
    if (!isEmpty(documentTemplate.additional_fields)) {
      setFieldForms(true);
    }
  }, [setFieldForms, documentTemplate.additional_fields]);

  const handleAddField = (values, setFieldValue) => {
    let newAdditionalFields = [...values.additional_fields];
    newAdditionalFields.push({
      label: '',
      field_type: 'text',
      default_value: '',
    });
    if (newAdditionalFields.length > 0) {
      setFieldForms(true);
    }
    setFieldValue('additional_fields', newAdditionalFields);
  };

  const handleRemoveField = (values, index, setFieldValue) => {
    let newAdditionalFields = [...values.additional_fields];
    newAdditionalFields.splice(index, 1);
    if (newAdditionalFields.length === 0) {
      setFieldForms(false);
    }
    setFieldValue('additional_fields', newAdditionalFields);
  };

  return (
    <Formik
      initialValues={{
        focusIndex: 0,
        additional_fields: documentTemplate.additional_fields || [],
      }}
      validate={validateForm}
      onSubmit={(values) => {
        setTemplate((template) => {
          return {
            ...template,
            ...values,
          };
        });
        forward();
      }}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <StyledForm fieldForms={fieldForms} {...rest}>
            <VerticalFormWrapper
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <H2 contentKey="landlordPortal.documentTemplate.additionalFields" centered />
              {fieldForms ? (
                <AdditionalFields
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  handleRemoveField={handleRemoveField}
                  documentTemplateTitle={documentTemplate.title}
                />
              ) : (
                <React.Fragment>
                  <StyledBodyContainer>
                    <React.Fragment>
                      <StyledIcon src={DocumentTemplateIcon} />
                      <StyledP
                        contentKey="landlordPortal.documentTemplate.addAdditionalFields"
                        centered
                      />
                    </React.Fragment>
                  </StyledBodyContainer>
                  <StyledButtonContainer>
                    <StyledButton
                      type="button"
                      contentKey="landlordPortal.documentTemplate.addField"
                      onClick={() => handleAddField(values, setFieldValue)}
                    />
                    <StyledButton contentKey="landlordPortal.inspections.next" />
                  </StyledButtonContainer>
                  <StyledFooterText>
                    {getTranslatedString('landlordPortal.documentTemplate.completedPagesAutoSave')}
                  </StyledFooterText>
                </React.Fragment>
              )}
            </VerticalFormWrapper>
          </StyledForm>
        );
      }}
    </Formik>
  );
};
